// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Portfolio financials — value & margin rolled up across every project.
// Pure functions over loaded project state; no React, no DB.
//
// Totals use the same simple-compounding markup model as BenchmarkView
// (the existing cross-project view) so the two never disagree. "Markup" is
// everything above direct cost (overhead + profit + contingency) — an
// unambiguous, computable figure — and margin% = markup / total.

type AnyItemState = Record<string, {
  qty?: number; unitCost?: number; included?: boolean; altType?: string; altStatus?: string;
}>;

export interface ProjectFinancials {
  id: string;
  name: string;
  subtotal: number;       // direct cost (sum of included line items)
  total: number;          // with overhead/profit/contingency applied
  markup: number;         // total − subtotal
  marginPct: number;      // markup / total
  overheadPct: number;
  profitPct: number;
  contingencyPct: number;
  area: number;
  costPerSF: number;
}

export interface PortfolioFinancials {
  projects: ProjectFinancials[];
  projectCount: number;
  totalDirectCost: number;
  totalContractValue: number;  // Σ totals
  totalMarkup: number;
  blendedMarginPct: number;    // totalMarkup / totalContractValue
  totalArea: number;
  blendedCostPerSF: number;    // totalContractValue / totalArea
}

function walkIds(rows: any[]): string[] {
  const ids: string[] = [];
  for (const r of rows || []) {
    if (r.type === "item" || (!r.type && r.id)) ids.push(r.id);
    if (r.rows) ids.push(...walkIds(r.rows));
    if (r.children) ids.push(...walkIds(r.children));
  }
  return ids;
}

export function extractProjectFinancials(project: {
  id: string;
  name: string;
  divisions?: any[];
  itemState?: AnyItemState | null;
  overhead?: number;
  profit?: number;
  contingency?: number;
  buildingArea?: number;
}): ProjectFinancials {
  const itemState = project.itemState || {};
  let subtotal = 0;
  for (const div of project.divisions || []) {
    for (const sub of div.subcategories || []) {
      for (const id of walkIds(sub.rows || [])) {
        const li = itemState[id];
        if (!li || !li.included) continue;
        // Skip voids and unaccepted alternates — matches the estimate's own rollup.
        if (li.altType && !(li.altType === "Alt" && li.altStatus === "Accepted")) continue;
        subtotal += (Number(li.qty) || 0) * (Number(li.unitCost) || 0);
      }
    }
  }
  const oh = Number(project.overhead) || 0;
  const pr = Number(project.profit) || 0;
  const cn = Number(project.contingency) || 0;
  const total = subtotal * (1 + oh / 100) * (1 + pr / 100) * (1 + cn / 100);
  const markup = total - subtotal;
  const area = Number(project.buildingArea) || 0;
  return {
    id: project.id,
    name: project.name,
    subtotal,
    total,
    markup,
    marginPct: total > 0 ? markup / total : 0,
    overheadPct: oh,
    profitPct: pr,
    contingencyPct: cn,
    area,
    costPerSF: area > 0 ? total / area : 0,
  };
}

export function computePortfolioFinancials(
  projects: Parameters<typeof extractProjectFinancials>[0][],
): PortfolioFinancials {
  const rows = projects.map(extractProjectFinancials).filter((p) => p.total > 0);
  // Largest projects first.
  rows.sort((a, b) => b.total - a.total);

  const totalDirectCost = rows.reduce((s, p) => s + p.subtotal, 0);
  const totalContractValue = rows.reduce((s, p) => s + p.total, 0);
  const totalMarkup = totalContractValue - totalDirectCost;

  // Blended $/SF divides over the projects that HAVE an area, on both sides.
  // Summing every project's contract value but only the recorded areas put a
  // project with no square footage into the numerator and not the denominator,
  // silently inflating the rate — a single estimate missing its area was
  // enough to throw the portfolio's headline number off.
  const measured = rows.filter((p) => p.area > 0);
  const totalArea = measured.reduce((s, p) => s + p.area, 0);
  const measuredValue = measured.reduce((s, p) => s + p.total, 0);

  return {
    projects: rows,
    projectCount: rows.length,
    totalDirectCost,
    totalContractValue,
    totalMarkup,
    blendedMarginPct: totalContractValue > 0 ? totalMarkup / totalContractValue : 0,
    totalArea,
    blendedCostPerSF: totalArea > 0 ? measuredValue / totalArea : 0,
  };
}
